import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { apiUrl } from '../../../core/config/api-endpoints';
import {
  Vendor,
  VendorDashboardCounts,
  VendorDeactivationDependenciesResponse,
  VendorListParams,
  VendorListResponse,
  VendorMutationResponse,
  VendorPayload,
  VendorPartnerListParams,
  VendorStatus,
  VendorStatusPayload,
} from '../models/vendor-management-row';
import { PartnerListResponse } from '../../partner-management/models/partner-management';

@Injectable({ providedIn: 'root' })
export class VendorService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = apiUrl('/portal/vendors');

  listVendors(params: VendorListParams): Observable<VendorListResponse> {
    return this.http.post<VendorListResponse>(
      `${this.baseUrl}/search`,
      this.buildListPayload(params),
    );
  }

  getDashboardCounts(): Observable<VendorDashboardCounts> {
    return this.http.get<VendorDashboardCounts>(`${this.baseUrl}/stats`);
  }

  getVendor(vendorId: string): Observable<Vendor> {
    return this.http.get<Vendor>(`${this.baseUrl}/${encodeURIComponent(vendorId)}`);
  }

  getVendorDeactivationDependencies(
    vendorId: string,
  ): Observable<VendorDeactivationDependenciesResponse> {
    return this.http.get<VendorDeactivationDependenciesResponse>(
      `${this.baseUrl}/${encodeURIComponent(vendorId)}/deactivation-dependencies`,
    );
  }

  listVendorPartners(
    vendorId: string,
    params: VendorPartnerListParams,
  ): Observable<PartnerListResponse> {
    return this.http.post<PartnerListResponse>(
      `${apiUrl('/portal/partners')}/vendor/${encodeURIComponent(vendorId)}`,
      this.buildVendorPartnerListPayload(params),
    );
  }

  createVendor(payload: VendorPayload): Observable<VendorMutationResponse> {
    return this.http.post<VendorMutationResponse>(this.baseUrl, payload);
  }

  updateVendor(vendorId: string, payload: VendorPayload): Observable<VendorMutationResponse> {
    return this.http.put<VendorMutationResponse>(
      `${this.baseUrl}/${encodeURIComponent(vendorId)}`,
      payload,
    );
  }

  updateVendorStatus(
    vendorId: string,
    statusOrPayload: VendorStatus | VendorStatusPayload,
  ): Observable<VendorMutationResponse> {
    const payload =
      typeof statusOrPayload === 'string' ? { status: statusOrPayload } : statusOrPayload;
    return this.http.patch<VendorMutationResponse>(
      `${this.baseUrl}/${encodeURIComponent(vendorId)}/status`,
      payload,
    );
  }

  private buildListPayload(params: VendorListParams): Record<string, string | number> {
    const payload: Record<string, string | number> = {
      page: params.page,
      limit: params.pageSize,
    };

    if (params.search?.trim()) {
      payload['search'] = params.search.trim();
    }

    if (params.statuses?.length) {
      payload['statuses'] = params.statuses.join(',');
    }

    if (params.onboardedFrom) {
      payload['onboarded_from'] = params.onboardedFrom;
    }

    if (params.onboardedTo) {
      payload['onboarded_to'] = params.onboardedTo;
    }

    return payload;
  }

  private buildVendorPartnerListPayload(params: VendorPartnerListParams): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      page: params.page,
      limit: params.pageSize,
      search: params.search,
      cities: params.cities,
      service_ids: params.serviceIds,
      statuses: params.statuses,
      document_statuses: params.documentStatuses,
      rating_min: params.ratingMin,
      rating_max: params.ratingMax,
      sla_min: params.slaMin,
      sla_max: params.slaMax,
      active_jobs_min: params.activeJobsMin,
      active_jobs_max: params.activeJobsMax,
    };

    for (const key of Object.keys(payload)) {
      const value = payload[key];
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete payload[key];
      }
    }

    return payload;
  }
}
